/**
 * /quiz-buzzer — a player's phone: one huge button.
 *
 * The press is stamped with the touch event's own time (not when the page got
 * round to it, not when the host receives it) and sent with that stamp. The
 * host answers clock pings (clock.js), so it can translate the stamp into its
 * own time, and it sends back this phone's offset — which lets the phone light
 * up at the SAME host instant as every other phone when the buttons open.
 *
 * The phone never decides whether a press counts: a press made a hair after
 * the opening may reach the phone before the «open» message does. It always
 * sends; the host rules.
 */
import * as snd from './sound.js';
import { t, $, el, msec, secs, num, clock, store, load, toast, stage, diag, funnel, rid, cleanName, PALETTE, inkOn, evStamp, vibrate, PREFIX, PROTO } from './util.js';

export function startPlayer(opts) {
    const RC = window.__RoomConnect;
    const code = opts.code;
    const room = load('room', null);
    const me = load('me', null) || {};
    const P = {
        code: code, room: null, conn: null, watch: null, tries: 0, timer: 0,
        pid: room && room.code === code && /^[a-f0-9]{16}$/.test(room.pid || '') ? room.pid : rid(8),
        boot: rid(6), seq: 0, o: null, e: null, rtt: null, view: null, pending: [],
        teams: [], scoring: 'players', used: [],
        name: cleanName(me.name || ''), color: PALETTE.includes(me.color) ? me.color : null, team: room && room.code === code ? room.team : null,
        joined: !!(room && room.code === code && room.joined && Date.now() - (room.at || 0) < 12 * 3600e3),
        sound: me.sound !== false, kicked: false, ended: null, lastState: '', armTimer: 0, kaTimer: 0, tickTimer: 0, linked: false
    };
    const form = $('qbJoin'), screen = $('qbPlayer');

    function saveRoom() { store('room', { code: code, pid: P.pid, team: P.team, joined: P.joined, at: Date.now() }); }

    // ── network ─────────────────────────────────────────────────────────────
    function send(m) { try { if (P.conn && P.conn.open) { P.conn.send(m); return true; } } catch (e) { /* lost */ } return false; }

    function connState(kind, text) {
        const js = $('qbJoinState');
        js.className = 'tc-msg ' + (kind === 'ok' ? 'good' : kind === 'err' ? 'bad' : 'warn');
        js.textContent = text;
        const bar = $('qbPlNet');
        bar.dataset.kind = kind;
        bar.textContent = kind === 'ok' ? '' : text;
        bar.hidden = kind === 'ok';
    }

    function connect() {
        if (P.kicked) return;
        clearTimeout(P.timer);
        if (P.room) { try { P.room.destroy(); } catch (e) { /* stale */ } }
        if (P.watch) { try { P.watch.stop(); } catch (e) { /* stale */ } P.watch = null; }
        P.conn = null;
        if (!RC || typeof window.Peer === 'undefined') { connState('err', t('net_missing')); diag('peerjs_missing', { side: 'player' }); return; }
        connState('wait', P.tries ? t('net_reconnecting') : t('net_connecting'));
        P.room = RC.joinRoom({
            prefix: PREFIX, code: code, tool: 'quiz-buzzer', enableVisibilityRecovery: true, timeoutMs: 15000,
            onConnected: function (conn) {
                P.conn = conn; P.tries = 0; P.linked = true;
                conn.on('data', onData);
                conn.on('close', lost);
                P.watch = RC.watchHost({ conn: conn, graceMs: 12000, onLost: lost, onBack: function () { connState('ok', ''); } });
                connState('ok', t('net_connected'));
                if (P.joined) hello();
                if (!P.firstLink) { P.firstLink = true; stage('player_linked'); }
            },
            onRoomNotFound: function (why) {
                connState('err', why === 'p2p_timeout' ? t('net_p2p') : t('net_no_room'));
                retry(6000);
            },
            onRefused: function () { connState('err', t('net_full')); },
            onError: function () { lost(); }
        });
    }
    function lost() {
        if (P.kicked) return;
        P.conn = null;
        connState('wait', t('net_reconnecting'));
        renderPlayer();
        retry();
    }
    function retry(base) {
        clearTimeout(P.timer);
        P.tries++;
        P.timer = setTimeout(connect, base || Math.min(10000, 800 * Math.pow(2, Math.min(4, P.tries - 1))));
    }
    document.addEventListener('visibilitychange', function () {
        if (!document.hidden && !(P.conn && P.conn.open) && !P.kicked) { P.tries = 0; connect(); }
    });
    window.addEventListener('online', function () { if (!(P.conn && P.conn.open) && !P.kicked) { P.tries = 0; connect(); } });

    function hello() {
        send({ t: 'hello', v: PROTO, pid: P.pid, boot: P.boot, name: P.name, color: P.color, team: P.team });
        // A press made while the link was down still counts — the host decides how.
        P.pending.forEach(function (m) { send(m); });
    }

    function onData(raw) {
        const pNow = performance.now();              // stamp first: this is the clock reading the host asked for
        let m = raw;
        if (typeof raw === 'string') { try { m = JSON.parse(raw); } catch (e) { return; } }
        if (!m || typeof m !== 'object') return;
        switch (m.t) {
            case 'sp':
                send({ t: 'sq', h: m.h, p: pNow });
                if (typeof m.o === 'number') { P.o = m.o; P.e = m.e; }
                break;
            case 'welcome':
                P.teams = Array.isArray(m.teams) ? m.teams : [];
                P.scoring = m.scoring || 'players';
                P.used = Array.isArray(m.used) ? m.used : [];
                if (!P.joined) renderForm(); else hello();
                break;
            case 'cfg':
                P.teams = Array.isArray(m.teams) ? m.teams : P.teams;
                P.scoring = m.scoring || P.scoring;
                if (!P.joined) renderForm();
                break;
            case 'st':
                if (typeof m.o === 'number') { P.o = m.o; P.e = m.e; }
                if (typeof m.rtt === 'number') P.rtt = m.rtt;
                P.view = m.v || null;
                if (P.view && P.view.ended === false) P.ended = null;
                if (P.view && P.view.color) P.color = P.view.color;
                renderPlayer();
                break;
            case 'ack':
                P.pending = P.pending.filter(function (x) { return x.s !== m.s; });
                break;
            case 'fx':
                if (m.k === 'correct') { vibrate([40, 40, 40, 40, 90]); if (P.sound) snd.play('correct'); flashFx('correct', m.d); }
                else if (m.k === 'wrong') { vibrate(350); if (P.sound) snd.play('wrong'); flashFx('wrong', m.d); }
                break;
            case 'end': P.ended = Array.isArray(m.top) ? m.top : []; renderPlayer(); break;
            case 'restart': P.ended = null; renderPlayer(); break;
            case 'kick':
                P.kicked = true;
                store('room', null);
                try { P.room && P.room.destroy(); } catch (e) { /* gone */ }
                connState('err', t('pl_kicked'));
                showScreen(false);
                form.hidden = false;
                $('qbJoinForm').hidden = true;
                break;
            case 'full': connState('err', t('net_full')); break;
            default: break;            // heartbeats
        }
    }

    // ── the join form ───────────────────────────────────────────────────────
    function renderForm() {
        form.hidden = false;
        document.documentElement.classList.add('qb-noscroll');
        $('qbJoinForm').hidden = false;
        $('qbJoinCodeShow').textContent = code.toUpperCase();
        const nm = $('qbName');
        if (!nm.value && P.name) nm.value = P.name;
        const box = $('qbColors');
        box.textContent = '';
        if (!P.color || (P.used.includes(P.color) && !P.joined)) P.color = PALETTE.find(function (c) { return !P.used.includes(c); }) || PALETTE[0];
        PALETTE.forEach(function (c) {
            const b = el('button', 'qb-sw qb-sw-big');
            b.type = 'button'; b.dataset.pick = c;
            b.style.setProperty('--c', c);
            b.setAttribute('aria-pressed', c === P.color ? 'true' : 'false');
            b.setAttribute('aria-label', t('color_n', { n: PALETTE.indexOf(c) + 1 }) + (P.used.includes(c) ? ' · ' + t('color_taken') : ''));
            if (P.used.includes(c)) b.classList.add('qb-taken');
            box.appendChild(b);
        });
        const tw = $('qbTeamWrap');
        tw.hidden = P.scoring !== 'teams' || !P.teams.length;
        const tb = $('qbTeamPick');
        tb.textContent = '';
        if (P.team && !P.teams.some(function (x) { return x.id === P.team; })) P.team = null;
        P.teams.forEach(function (tm) {
            const b = el('button', 'tc-chip' + (tm.id === P.team ? ' on' : ''), tm.name);
            b.type = 'button'; b.dataset.tpick = tm.id;
            b.style.setProperty('--c', tm.color);
            b.setAttribute('aria-pressed', tm.id === P.team ? 'true' : 'false');
            tb.appendChild(b);
        });
        $('qbPlSound').checked = P.sound;
        stage('join_form');
    }
    form.addEventListener('click', function (e) {
        const b = e.target.closest ? e.target.closest('button') : null;
        if (!b) return;
        if (b.dataset.pick) { P.color = b.dataset.pick; renderForm(); }
        else if (b.dataset.tpick) { P.team = b.dataset.tpick; renderForm(); }
    });
    $('qbReady').addEventListener('click', function () {
        const name = cleanName($('qbName').value);
        if (!name) { $('qbName').focus(); toast(t('name_needed'), 'warning'); return; }
        if (P.scoring === 'teams' && P.teams.length && !P.team) { toast(t('team_needed'), 'warning'); return; }
        P.name = name;
        P.sound = $('qbPlSound').checked;
        P.joined = true;
        store('me', { name: P.name, color: P.color, sound: P.sound });
        saveRoom();
        snd.unlock();
        if (P.sound) snd.play('press');
        try { if (window.__wakeLock) window.__wakeLock.request(); } catch (e) { /* optional */ }
        if (window.matchMedia && window.matchMedia('(pointer:coarse)').matches && window.__safeFullscreen) window.__safeFullscreen(screen);
        hello();
        form.hidden = true;
        showScreen(true);
        stage('joined');
        funnel('player_ready', { team: P.team ? 1 : 0 });
    });
    $('qbJoinClose').addEventListener('click', function () {
        store('room', null);
        try { if (P.room) P.room.destroy(); } catch (e) { /* not connected */ }
        location.href = location.pathname;
    });
    $('qbName').addEventListener('keydown', function (e) { if (e.key === 'Enter') { e.preventDefault(); $('qbReady').click(); } });

    // ── the button screen ───────────────────────────────────────────────────
    function showScreen(on) {
        screen.hidden = !on;
        document.documentElement.classList.toggle('qb-noscroll', !!on || !form.hidden);
        if (on) renderPlayer();
    }
    /* «+37 ms» as one left-to-right run: in Arabic the sign would jump to the far end. */
    function isoPlus(text, g) { return text.split('+' + g).join('\u2066+' + g + '\u2069'); }
    function toLocal(h) { return (typeof h === 'number' && P.o != null) ? h + P.o : null; }

    function renderPlayer() {
        if (screen.hidden) return;
        const v = P.view;
        const n = performance.now();
        let state = 'connecting', main = t('net_connecting'), sub = '';
        clearTimeout(P.armTimer);
        if (P.ended) {
            state = 'ended';
            main = t('pl_ended');
            const mine = v ? P.ended.find(function (r) { return r.n === v.entName; }) : null;
            sub = mine ? t('pl_place', { n: mine.p, of: P.ended.length, s: num(mine.s) }) : '';
        } else if (!v) {
            state = P.conn ? 'closed' : 'connecting';
            main = P.conn ? t('pl_closed') : t('net_connecting');
        } else {
            state = v.state;
            switch (v.state) {
                case 'armed': {
                    const la = toLocal(v.armAt);
                    if (la != null && la > n + 4) {
                        state = 'ready'; main = t('pl_ready'); sub = t('pl_ready_sub');
                        P.armTimer = setTimeout(renderPlayer, la - n);
                    } else { main = t('pl_armed'); sub = t('pl_armed_sub'); }
                    break;
                }
                case 'pressed': main = t('pl_pressed'); sub = t('pl_pressed_sub'); break;
                case 'first': main = t('pl_first'); sub = t('pl_first_sub'); break;
                case 'team_first': main = t('pl_team_first'); sub = t('pl_first_sub'); break;
                case 'rank':
                    main = t('pl_rank_big', { n: v.rank });
                    sub = isoPlus(v.tie ? t('pl_tie', { gap: msec(v.gap) }) : t('pl_gap', { gap: msec(v.gap) }), msec(v.gap));
                    if (v.late) sub += ' · ' + t('pl_late');
                    break;
                case 'penalty': {
                    const lu = toLocal(v.lockUntil);
                    main = t('pl_penalty');
                    sub = lu != null ? t('pl_penalty_sub', { s: secs(Math.max(0, lu - n)) }) : '';
                    if (lu != null && lu > n) P.armTimer = setTimeout(renderPlayer, Math.min(250, lu - n));
                    break;
                }
                case 'out': main = v.reason === 'false' ? t('pl_out_false') : t('pl_out'); sub = t('pl_out_sub'); break;
                case 'wait': main = t('pl_wait'); sub = v.answerer ? t('pl_wait_other', { name: v.answerer }) : ''; break;
                case 'extra': main = t('pl_extra'); sub = t('pl_extra_sub'); break;
                default: main = t('pl_closed'); sub = v.answerer ? t('pl_wait_other', { name: v.answerer }) : t('pl_closed_sub');
            }
        }
        screen.dataset.state = state;
        const color = (v && v.color) || P.color || '#3b82f6';
        screen.style.setProperty('--c', color);
        screen.style.setProperty('--ink', inkOn(color));
        $('qbPlMain').textContent = main;
        $('qbPlSub').textContent = sub;
        $('qbPlName').textContent = P.name || '';
        $('qbPlTeam').textContent = v && v.team ? v.entName : '';
        $('qbPlTeam').hidden = !(v && v.team);
        $('qbPlScore').textContent = v ? num(v.score) : '0';
        $('qbPlPlace').textContent = v && v.place ? t('pl_place_short', { n: v.place, of: v.of }) : '';
        $('qbPlSync').textContent = P.e != null && P.conn ? t('pl_sync', { e: msec(P.e) }) : '';
        const btn = $('qbPlBtn');
        btn.setAttribute('aria-label', main + (sub ? '. ' + sub : ''));
        // timer
        const tm = v && v.timer;
        const tl = $('qbPlTimer');
        if (tm && toLocal(tm.endsAt) != null && !P.ended) {
            tl.hidden = false;
            const left = Math.max(0, toLocal(tm.endsAt) - Math.max(n, toLocal(v.armAt) || n));
            tl.textContent = clock(left);
            clearTimeout(P.tickTimer);
            if (left > 0) P.tickTimer = setTimeout(renderPlayer, 250);
        } else tl.hidden = true;
        // final standings
        const fin = $('qbPlFinal');
        fin.hidden = !P.ended;
        if (P.ended) {
            fin.textContent = '';
            P.ended.slice(0, 5).forEach(function (r) {
                const li = el('li', null, r.p + '. ' + r.n + ' — ' + num(r.s));
                li.style.setProperty('--c', r.c || '#888');
                fin.appendChild(li);
            });
        }
        feedback(state);
        keepAlive(v && v.phase === 'armed');
    }

    function feedback(state) {
        if (state === P.lastState) return;
        const prev = P.lastState;
        P.lastState = state;
        if (state === 'armed') vibrate(45);
        else if (state === 'first' || state === 'team_first') { vibrate([90, 60, 90]); if (P.sound) snd.play('win'); }
        else if (state === 'rank') vibrate(25);
        else if (state === 'penalty' || (state === 'out' && prev === 'pressed')) vibrate(250);
    }

    // While the buttons are open the phone chatters a little: a Wi-Fi radio
    // that has just sent something stays awake, and a sleeping one can hold a
    // packet — the press — for a beacon interval or two.
    function keepAlive(on) {
        if (on && !P.kaTimer) P.kaTimer = setInterval(function () { send({ t: 'ka' }); }, 150);
        else if (!on && P.kaTimer) { clearInterval(P.kaTimer); P.kaTimer = 0; }
    }

    function flashFx(kind, delta) {
        const f = $('qbPlFx');
        f.dataset.kind = kind;
        f.textContent = (kind === 'correct' ? t('fx_correct') : t('fx_wrong')) + (delta ? ' ' + (delta > 0 ? '+' : '−') + num(Math.abs(delta)) : '');
        f.hidden = false;
        clearTimeout(P.fxTimer);
        P.fxTimer = setTimeout(function () { f.hidden = true; }, 2200);
    }

    function press(e) {
        const ts = evStamp(e);
        if (P.ended || P.kicked) return;
        P.seq++;
        P.lastPressAt = ts;
        const m = { t: 'pr', s: P.seq, p: ts };
        P.pending.push(m);
        if (P.pending.length > 5) P.pending.shift();
        send(m);
        vibrate(18);
        if (P.sound) snd.play('press');
        const b = $('qbPlBtn');
        b.classList.remove('qb-hit'); void b.offsetWidth; b.classList.add('qb-hit');
        if (P.view && P.view.state === 'armed') { P.view = Object.assign({}, P.view, { state: 'pressed' }); renderPlayer(); }
        if (!P.pressedOnce) { P.pressedOnce = true; stage('pressed'); }
    }
    const btn = $('qbPlBtn');
    btn.addEventListener('pointerdown', function (e) {
        if (e.button > 0) return;
        e.preventDefault();
        snd.unlock();
        press(e);
    });
    btn.addEventListener('contextmenu', function (e) { e.preventDefault(); });
    document.addEventListener('keydown', function (e) {
        if (screen.hidden || e.repeat || e.ctrlKey || e.metaKey || e.altKey) return;
        if (e.code === 'Space' || e.code === 'Enter' || e.code === 'NumpadEnter') { e.preventDefault(); press(e); }
    });
    $('qbPlLeave').addEventListener('click', function () {
        if (!window.confirm(t('pl_leave_confirm'))) return;
        send({ t: 'bye' });
        P.kicked = true;
        store('room', null);
        setTimeout(function () { try { P.room && P.room.destroy(); } catch (e) { /* gone */ } location.href = location.pathname; }, 150);
    });
    $('qbPlFs').addEventListener('click', function () {
        if (document.fullscreenElement) { try { document.exitFullscreen(); } catch (e) { /* not fullscreen */ } }
        else if (window.__safeFullscreen) window.__safeFullscreen(screen);
    });
    $('qbPlSnd').addEventListener('click', function () {
        P.sound = !P.sound;
        store('me', { name: P.name, color: P.color, sound: P.sound });
        $('qbPlSnd').textContent = P.sound ? '🔊' : '🔇';
        $('qbPlSnd').setAttribute('aria-pressed', P.sound ? 'true' : 'false');
        if (P.sound) { snd.unlock(); snd.play('press'); }
    });
    $('qbPlSnd').textContent = P.sound ? '🔊' : '🔇';
    $('qbPlCode').textContent = code.toUpperCase();

    // ── go ──────────────────────────────────────────────────────────────────
    saveRoom();
    if (P.joined) { showScreen(true); }
    else renderForm();
    connect();
    window.__qbPlayer = P;
    return P;
}
